import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useTusUpload, type TusUploadState, type TusUploadStatus } from '../hooks/useTusUpload';

interface FileUploadProps {
  onUploadComplete?: (filePath: string, fileName: string, fileSize?: number, mimeType?: string) => void;
  acceptedFileTypes?: 'audio' | 'transcript' | 'all';
  maxSizeMB?: number;
}

interface UploadingFile {
  file: File;
  progress: number;
  error?: string;
  uploadedPath?: string;
  /** Whether this file uses TUS resumable upload */
  isTus?: boolean;
  /** TUS-specific status for pause/resume controls */
  tusStatus?: TusUploadStatus;
  /** Real upload speed in bytes/sec (TUS only) */
  bytesPerSecond?: number;
  /** Bytes uploaded so far (TUS only) */
  bytesUploaded?: number;
}

/** Format bytes/sec into a human-readable speed string */
function formatSpeed(bytesPerSecond: number): string {
  if (bytesPerSecond <= 0) return '';
  if (bytesPerSecond < 1024) return `${Math.round(bytesPerSecond)} B/s`;
  if (bytesPerSecond < 1024 * 1024)
    return `${(bytesPerSecond / 1024).toFixed(1)} KB/s`;
  return `${(bytesPerSecond / (1024 * 1024)).toFixed(1)} MB/s`;
}

/** Estimate remaining time given bytes left and current speed */
function formatEta(bytesRemaining: number, bytesPerSecond: number): string {
  if (bytesPerSecond <= 0) return '';
  const seconds = Math.ceil(bytesRemaining / bytesPerSecond);
  if (seconds < 60) return `${seconds}s remaining`;
  if (seconds < 3600)
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s remaining`;
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m remaining`;
}

export default function FileUpload({
  onUploadComplete,
  acceptedFileTypes = 'all',
  maxSizeMB = 500,
}: FileUploadProps) {
  const { user } = useAuth();
  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [tusState, tusActions] = useTusUpload();

  // Ref to always have the latest tusState inside setInterval closures
  const tusStateRef = useRef<TusUploadState>(tusState);
  useEffect(() => {
    tusStateRef.current = tusState;
  }, [tusState]);

  // Define accepted file types
  // Note: Only MP3 is supported for audio to enable fast server-side concatenation
  const acceptConfig = {
    audio: {
      'audio/mpeg': ['.mp3'],
    },
    transcript: {
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    all: {
      'audio/mpeg': ['.mp3'],
      'text/plain': ['.txt'],
      'application/rtf': ['.rtf'],
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
  };

  /** Standard (non-TUS) upload for small files */
  const uploadFileStandard = async (file: File) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `${user.id}/${timestamp}_${sanitizedFileName}`;

    const { data, error } = await supabase.storage
      .from('session-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false,
      });

    if (error) {
      throw error;
    }

    return {
      path: data.path,
      originalName: file.name,
      size: file.size,
      mimeType: file.type,
    };
  };

  /** TUS resumable upload for large files (>6MB) */
  const uploadFileTus = async (file: File, fileIndex: number, totalFiles: number) => {
    if (!user) {
      throw new Error('User not authenticated');
    }

    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const objectPath = `${user.id}/${timestamp}_${sanitizedFileName}`;
    const bucket = 'session-files';

    console.log(
      `[FileUpload] Starting TUS resumable upload: ${file.name} (${(file.size / (1024 * 1024)).toFixed(2)}MB)`
    );

    // Poll tusStateRef (not tusState — avoids stale closure) for progress updates
    const progressInterval = setInterval(() => {
      const current = tusStateRef.current;
      setUploadingFiles((prev) =>
        prev.map((uf, idx) => {
          if (idx !== prev.length - totalFiles + fileIndex) return uf;
          if (!uf.isTus) return uf;
          return {
            ...uf,
            progress: current.progress,
            bytesPerSecond: current.bytesPerSecond,
            bytesUploaded: current.bytesUploaded,
            tusStatus: current.status,
          };
        })
      );
    }, 200);

    try {
      const storagePath = await tusActions.startUpload(file, bucket, objectPath, {
        upsert: false,
        cacheControl: '3600',
      });

      clearInterval(progressInterval);

      return {
        path: storagePath,
        originalName: file.name,
        size: file.size,
        mimeType: file.type,
      };
    } catch (error) {
      clearInterval(progressInterval);
      throw error;
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newUploadingFiles: UploadingFile[] = acceptedFiles.map((file) => ({
      file,
      progress: 0,
      isTus: tusActions.shouldUseTus(file.size),
      tusStatus: tusActions.shouldUseTus(file.size) ? 'uploading' : undefined,
    }));

    setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

    for (let i = 0; i < acceptedFiles.length; i++) {
      const file = acceptedFiles[i];

      if (!file) continue;

      const useTus = tusActions.shouldUseTus(file.size);
      const fileSizeMB = file.size / (1024 * 1024);

      try {
        if (useTus) {
          // --- TUS resumable upload (real progress) ---
          console.log(
            `[FileUpload] Using TUS for ${file.name} (${fileSizeMB.toFixed(2)}MB)`
          );

          const result = await uploadFileTus(file, i, acceptedFiles.length);

          // Mark complete
          setUploadingFiles((prev) =>
            prev.map((uf, idx) =>
              idx === prev.length - acceptedFiles.length + i
                ? { ...uf, progress: 100, uploadedPath: result.path, tusStatus: 'complete' }
                : uf
            )
          );

          console.log(`[FileUpload] TUS upload complete: ${file.name}`);

          if (onUploadComplete) {
            await onUploadComplete(result.path, result.originalName, result.size, result.mimeType);
          }
        } else {
          // --- Standard upload for small files (<6MB) ---
          console.log(
            `[FileUpload] Using standard upload for ${file.name} (${fileSizeMB.toFixed(2)}MB)`
          );

          const estimatedTimeMs = Math.max(1000, fileSizeMB * 1000);
          const progressIntervalMs = Math.max(100, estimatedTimeMs / 90);

          const progressInterval = setInterval(() => {
            setUploadingFiles((prev) =>
              prev.map((uf, idx) =>
                idx === prev.length - acceptedFiles.length + i && uf.progress < 90
                  ? { ...uf, progress: Math.min(90, uf.progress + 1) }
                  : uf
              )
            );
          }, progressIntervalMs);

          const result = await uploadFileStandard(file);
          clearInterval(progressInterval);

          setUploadingFiles((prev) =>
            prev.map((uf, idx) =>
              idx === prev.length - acceptedFiles.length + i
                ? { ...uf, progress: 100, uploadedPath: result.path }
                : uf
            )
          );

          console.log(`[FileUpload] Standard upload complete: ${file.name}`);

          if (onUploadComplete) {
            await onUploadComplete(result.path, result.originalName, result.size, result.mimeType);
          }
        }

        // Remove from list after 3 seconds
        setTimeout(() => {
          setUploadingFiles((prev) =>
            prev.filter((_, idx) => idx !== prev.length - acceptedFiles.length + i)
          );
        }, 3000);
      } catch (error) {
        setUploadingFiles((prev) =>
          prev.map((uf, idx) =>
            idx === prev.length - acceptedFiles.length + i
              ? { ...uf, error: (error as Error).message, tusStatus: 'error' }
              : uf
          )
        );
      }
    }
  }, [user, onUploadComplete, tusActions]);

  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    onDrop,
    accept: acceptConfig[acceptedFileTypes],
    maxSize: maxSizeMB * 1024 * 1024,
    multiple: true,
  });

  return (
    <div className="w-full">
      {/* Dropzone */}
      <div
        {...getRootProps()}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors
          ${isDragActive ? 'border-orca-elevate bg-orca-elevate/10' : 'border-steel/30 hover:border-orca-elevate/50'}
        `}
      >
        <input {...getInputProps()} />

        <div className="flex flex-col items-center space-y-4">
          {/* Upload Icon */}
          <svg
            className={`w-16 h-16 ${isDragActive ? 'text-orca-elevate' : 'text-slate/50'}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
            />
          </svg>

          {/* Instructions */}
          <div>
            <p className="text-lg font-medium text-navy mb-1">
              {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
            </p>
            <p className="text-sm text-steel">
              or click to browse
            </p>
          </div>

          {/* Accepted formats */}
          <div className="text-xs text-steel/70">
            {acceptedFileTypes === 'audio' && (
              <p>Accepted: MP3 (max {maxSizeMB}MB)</p>
            )}
            {acceptedFileTypes === 'transcript' && (
              <p>Accepted: TXT, RTF, PDF, DOCX (max {maxSizeMB}MB)</p>
            )}
            {acceptedFileTypes === 'all' && (
              <p>Accepted: Audio (MP3) or Transcript (TXT, RTF, PDF, DOCX) - max {maxSizeMB}MB</p>
            )}
            <p className="mt-1 text-steel/50">
              Large files use resumable upload (pause/resume supported)
            </p>
          </div>
        </div>
      </div>

      {/* File Rejections */}
      {fileRejections.length > 0 && (
        <div className="mt-4 space-y-2">
          {fileRejections.map(({ file, errors }) => (
            <div key={file.name} className="bg-red-50 border border-red-200 rounded-md p-3">
              <p className="text-sm font-medium text-red-800">{file.name}</p>
              {errors.map((error) => (
                <p key={error.code} className="text-xs text-red-600 mt-1">
                  {error.code === 'file-too-large'
                    ? `File is too large. Max size: ${maxSizeMB}MB`
                    : error.code === 'file-invalid-type'
                    ? 'Invalid file type'
                    : error.message}
                </p>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Uploading Files */}
      {uploadingFiles.length > 0 && (
        <div className="mt-4 space-y-3">
          {uploadingFiles.map((uploadingFile, idx) => (
            <div key={idx} className="bg-white border border-steel/20 rounded-md p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-navy truncate flex-1">
                  {uploadingFile.file.name}
                </span>
                <div className="flex items-center gap-2 ml-2">
                  {uploadingFile.isTus && (
                    <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">
                      Resumable
                    </span>
                  )}
                  <span className="text-xs text-steel">
                    {(uploadingFile.file.size / 1024 / 1024).toFixed(2)} MB
                  </span>
                </div>
              </div>

              {/* Progress Bar */}
              {!uploadingFile.error && (
                <div className="w-full bg-steel/10 rounded-full h-2 mb-2">
                  <div
                    className={`h-2 rounded-full transition-all duration-300 ${
                      uploadingFile.progress === 100
                        ? 'bg-green-500'
                        : uploadingFile.tusStatus === 'paused'
                        ? 'bg-amber-400'
                        : 'bg-orca-elevate'
                    }`}
                    style={{ width: `${uploadingFile.progress}%` }}
                  />
                </div>
              )}

              {/* Status + Speed (TUS) */}
              <div className="flex items-center justify-between text-xs">
                <div>
                  {uploadingFile.error ? (
                    <span className="text-red-600">{uploadingFile.error}</span>
                  ) : uploadingFile.progress === 100 ? (
                    <span className="text-green-600">Upload complete - linking to session...</span>
                  ) : uploadingFile.tusStatus === 'paused' ? (
                    <span className="text-amber-600">Paused at {uploadingFile.progress}%</span>
                  ) : uploadingFile.progress >= 90 && !uploadingFile.isTus ? (
                    <span className="text-orca-elevate">Finalizing upload...</span>
                  ) : (
                    <span className="text-steel">Uploading... {uploadingFile.progress}%</span>
                  )}
                </div>

                {/* Speed & ETA for TUS uploads */}
                {uploadingFile.isTus &&
                  uploadingFile.tusStatus === 'uploading' &&
                  uploadingFile.bytesPerSecond != null &&
                  uploadingFile.bytesPerSecond > 0 && (
                    <span className="text-steel/70">
                      {formatSpeed(uploadingFile.bytesPerSecond)}
                      {' · '}
                      {formatEta(
                        uploadingFile.file.size - (uploadingFile.bytesUploaded ?? 0),
                        uploadingFile.bytesPerSecond
                      )}
                    </span>
                  )}
              </div>

              {/* Pause / Resume / Cancel controls (TUS only) */}
              {uploadingFile.isTus &&
                !uploadingFile.error &&
                uploadingFile.progress < 100 && (
                  <div className="flex gap-2 mt-2">
                    {uploadingFile.tusStatus === 'uploading' && (
                      <button
                        type="button"
                        onClick={() => tusActions.pause()}
                        className="text-xs px-2 py-1 rounded border border-steel/30 text-steel hover:bg-steel/5 transition-colors"
                      >
                        Pause
                      </button>
                    )}
                    {uploadingFile.tusStatus === 'paused' && (
                      <button
                        type="button"
                        onClick={() => tusActions.resume()}
                        className="text-xs px-2 py-1 rounded border border-orca-elevate/50 text-orca-elevate hover:bg-orca-elevate/5 transition-colors"
                      >
                        Resume
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => tusActions.cancel()}
                      className="text-xs px-2 py-1 rounded border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                )}

              {/* Retry button on error (TUS) */}
              {uploadingFile.isTus && uploadingFile.error && (
                <div className="mt-2">
                  <button
                    type="button"
                    onClick={() => tusActions.resume()}
                    className="text-xs px-2 py-1 rounded border border-orca-elevate/50 text-orca-elevate hover:bg-orca-elevate/5 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
